import { displayDigits } from '../utils/displayDigits';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmDatePickerModal } from './NmDatePickerModal';
import { normalizeLocale } from '../i18n/config';
import {
  formatPickerPreview,
  isJalaliPickerLocale,
  jalaliKeyToPickerDate,
} from '../utils/calendarPickerLocale';
import { formatJalaliDateLabel } from '../utils/homeHelpers';
import { isoToJalaliKey, jalaliKeyToIso } from '../utils/jalali';

export interface NmIsoDateFieldProps {
  /** Gregorian ISO date (`YYYY-MM-DD`) for API/storage. */
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  /** When inside workout detail panel — must exceed z-index 1501. */
  sheetZIndex?: number;
  portalContainer?: HTMLElement | null;
}

/** ISO date field — locale-aware neumorphic picker (Jalali for `fa`, Gregorian otherwise). */
export function NmIsoDateField({
  value,
  onChange,
  min,
  max,
  required,
  className = '',
  disabled,
  placeholder,
  style,
  ariaLabel,
  sheetZIndex,
  portalContainer,
}: NmIsoDateFieldProps) {
  const { t, i18n } = useTranslation();
  const localeCode = normalizeLocale(i18n.language);
  const useJalali = isJalaliPickerLocale(localeCode);
  const [open, setOpen] = useState(false);
  const dateValue = value.trim().split(/[T ]/)[0];

  const displayLabel = useMemo(() => {
    if (!dateValue) {
      return placeholder ?? t('calendar.datePicker.title', 'Select date');
    }
    if (useJalali) {
      return formatJalaliDateLabel(new Date(`${dateValue}T00:00:00`));
    }
    const key = isoToJalaliKey(dateValue);
    const parsed = jalaliKeyToPickerDate(key, false);
    if (!parsed) return dateValue;
    return formatPickerPreview(parsed, false, localeCode);
  }, [localeCode, placeholder, t, useJalali, dateValue]);

  const pickerValue = useMemo(() => {
    if (dateValue) return isoToJalaliKey(dateValue);
    return isoToJalaliKey(new Date().toISOString().slice(0, 10));
  }, [dateValue]);

  const handleConfirm = (jalaliKey: string) => {
    const iso = jalaliKeyToIso(jalaliKey);
    if (!iso) return;
    if (min && iso < min) return;
    if (max && iso > max) return;
    onChange(iso);
  };

  return (
    <>
      <button
        type="button"
        className={`nm-input-field nm-iso-date-field ${className}`.trim()}
        style={style}
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-required={required || undefined}
      >
        {displayDigits(displayLabel, i18n.language)}
      </button>
      <NmDatePickerModal
        open={open}
        value={pickerValue}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
        min={min?.split(/[T ]/)[0]}
        max={max?.split(/[T ]/)[0]}
        zIndex={sheetZIndex}
        portalContainer={portalContainer}
      />
    </>
  );
}
